/* eslint-disable max-lines */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSearch } from '@okapi/router-react';
import { useUpdateEffect } from 'common/hooks';
import { useDebounced } from 'common/hooks/use-debounced';
import { Suggestions } from 'types/suggestions';
import { dynamicYields, analytics } from 'utils';
import { RouterContext } from '@okapi/router-react/dist/Provider';
import { useStoreon } from 'storeon/react';
import { ProductRedirectionUrl } from 'types/product-url';
import { Region } from 'types/model';
import { ProductRedirectionEvents } from 'actions';
import { rrHelper } from 'utils/retail-rocket';
import { useKeyboardNavigation } from 'common/hooks/use-keyboard-navigation';
import { sanitizeString } from '../utils/sanitize-string';
import { getDefaultSlicedSuggestions, getSlicedSuggestions } from './utils/get-sliced-suggestions';
import { TransitionState } from '../types';

export type UseSearchbarLogicParams = {
  getSuggestions: (value: string) => void;
  resetServices: () => void;
  collapse: () => void;
  transitionState: TransitionState;
  suggestions: Suggestions | null;
  suggestionsLoading?: boolean;
  error?: boolean;
  productRedirectionUrl?: ProductRedirectionUrl['redirectionUrl'];
  productRedirectionUrlLoading?: boolean;
  currentRegionId?: Region['regionId'];
};

export const useSearchbarLogic = (params: UseSearchbarLogicParams) => {
  const {
    getSuggestions,
    resetServices,
    transitionState,
    collapse,
    productRedirectionUrl,
    productRedirectionUrlLoading,
    currentRegionId,
    suggestions,
  } = params;

  const router = useContext(RouterContext);
  const path = router.getPath();
  const { navigate } = router;
  const { dispatch } = useStoreon();

  const [search, setSearch] = useSearch();
  const [isSrp, setIsSrp] = useState(path.startsWith('/search'));

  const [initialValue, setInitialValue] = useState((search.q as string) ?? '');
  const [value, setValue] = useState(initialValue);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { slicedSuggestions, flatSuggestions } = useMemo(() => {
    if (suggestions) {
      const suggestionSlice = getSlicedSuggestions(suggestions.products, suggestions.services);
      const defaultSuggestionsSlice = getDefaultSlicedSuggestions(suggestions.products);

      return value
        ? {
            slicedSuggestions: suggestionSlice,
            flatSuggestions: [...suggestionSlice[0], ...suggestionSlice[1]],
          }
        : {
            slicedSuggestions: defaultSuggestionsSlice,
            flatSuggestions: [
              ...defaultSuggestionsSlice[2],
              ...defaultSuggestionsSlice[0],
              ...defaultSuggestionsSlice[1],
            ],
          };
    }

    return {
      slicedSuggestions: null,
      flatSuggestions: null,
    };
  }, [suggestions, value]);

  const {
    index,
    reset,
    selectedItem: selectedSuggestion,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
  } = useKeyboardNavigation(flatSuggestions, { canUnselectAllItems: true });

  const handleGetSuggestions = useCallback(
    (searchString: string) => {
      getSuggestions(searchString);
      if (searchString) dynamicYields.search(searchString);
    },
    [getSuggestions]
  );

  const dGetSuggestions = useDebounced(handleGetSuggestions, { interval: 300 });

  const prettifiedValue = useMemo(() => sanitizeString(value), [value]);

  const isLmcode = useMemo(() => /^\d+$/.test(prettifiedValue) && prettifiedValue.length === 8, [
    prettifiedValue,
  ]);

  const sanitizedValue = selectedSuggestion?.phrase ?? prettifiedValue;

  useUpdateEffect(() => {
    setInitialValue((search.q as string) ?? '');
  }, [search]);

  useUpdateEffect(() => {
    setIsSrp(path.startsWith('/search'));
  }, [path]);

  useUpdateEffect(() => {
    if (isLmcode && !productRedirectionUrlLoading && isSubmitted) {
      if (productRedirectionUrl) {
        navigate(productRedirectionUrl);
        dispatch(ProductRedirectionEvents.refresh);
        setIsSubmitted(false);
        collapse();
      } else {
        navigate(`/search?q=${sanitizedValue}`);
        setIsSubmitted(false);
        collapse();
      }
    }
  }, [isLmcode, productRedirectionUrlLoading, productRedirectionUrl]);

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      setIsSubmitted(true);
      rrHelper.dispatchSearchEvent(value);

      if (sanitizedValue) {
        analytics.sendEvent('GA:click', { position: 'search', name: value });

        dynamicYields.search(value);

        if (isLmcode && !productRedirectionUrl && !productRedirectionUrlLoading) {
          e.preventDefault();
          dispatch(ProductRedirectionEvents.request, { lmcode: sanitizedValue, regionId: currentRegionId });
        }

        if (selectedSuggestion?.phrase != null) {
          setSearch({
            q: sanitizedValue,
            ...(selectedSuggestion.phrase && { q: selectedSuggestion.phrase }),
            ...(selectedSuggestion && { suggest: 'true' }),
          });
          if (isSrp) {
            e.preventDefault();
            collapse();
          }
        }

        if (selectedSuggestion?.categoryUrl != null) {
          e.preventDefault();
          navigate(selectedSuggestion.categoryUrl);
          collapse();
        }
        return;
      }
      e.preventDefault();
    },
    [
      sanitizedValue,
      value,
      isLmcode,
      productRedirectionUrl,
      productRedirectionUrlLoading,
      isSrp,
      selectedSuggestion,
      dispatch,
      currentRegionId,
      setSearch,
      collapse,
      navigate,
    ]
  );
  const updateValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue.replace(/(<([^>]+)>)/gi, ''));
      reset();
      if (nextValue === '') resetServices();
      dGetSuggestions(nextValue);
    },
    [dGetSuggestions, reset, resetServices]
  );

  useUpdateEffect(() => {
    if (transitionState === 'collapsed') {
      reset();
      updateValue(initialValue);
    }
  }, [initialValue, updateValue, transitionState, reset]);

  return {
    isSrp,
    onSubmit,
    value,
    updateValue,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    index,
    selectedSuggestion,
    slicedSuggestions,
  };
};
